import { useMobileNavigation } from "@/context/MobileNavigationContext";
import "./Header.css";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, User as UserIcon, ChevronDown, ExternalLink, Settings, UserCircle2, MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { getExpertApprovalStatus, hasApprovedExpertAccess } from "@/lib/expertAccess";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import ProfileMenu from "@/components/ProfileMenu";
import { HeaderSkeleton } from "@/components/LoadingSkeleton";
import BrandLogo from "@/components/BrandLogo";
import {
  CUSTOMER_NAV,
  EXPERT_PRIMARY,
  MOBILE_CUSTOMER_NAV,
  MOBILE_PUBLIC_NAV,
  PUBLIC_NAV,
} from "@/lib/headerNavigation";
import { trackEvent } from "@/lib/analytics";
import { requestEntryUrl } from "@/lib/requestEntry";

export default function Header() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const requestUrl = requestEntryUrl(loc.search, { seo_landing: loc.pathname.match(/^\/hjaelp\/([^/]+)\/?$/)?.[1] });
  const { open, setOpen } = useMobileNavigation();
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expertProfile, setExpertProfile] = useState(user?.expert_profile || null);
  const [reqCount, setReqCount] = useState(0);
  const [activeRequestCount, setActiveRequestCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const menuRef = useRef(null);

  const isCustomer = user?.role === "customer";
  const isExpert = hasApprovedExpertAccess(user);
  const isPendingExpert = user?.role === "expert" && getExpertApprovalStatus(user) === "pending";
  const isUnapprovedExpert = user?.role === "expert" && !isExpert;
  const isAdmin = user?.role === "admin";
  const visibleExpertProfile = expertProfile || user?.expert_profile || null;
  const expertProfilePath = isPendingExpert
    ? "/expert/min-profil"
    : isExpert
      ? "/expert?tab=profile"
      : "/expert/onboarding";
  const expertSettingsPath = isExpert ? "/expert?tab=settings" : "/expert/indstillinger";


  // Load expert profile + request count for header widgets.
  useEffect(() => {
    if (!isExpert) return;
    let stop = false;
    const load = async () => {
      try {
        const [meRes, reqRes] = await Promise.all([
          api.get("/auth/me"),
          api.get("/expert/requests"),
        ]);
        if (stop) return;
        setExpertProfile(meRes.data?.expert_profile || null);
        setReqCount((reqRes.data || []).length);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 12000);
    return () => { stop = true; clearInterval(t); };
  }, [isExpert, user?.id]);

  useEffect(() => {
    if (!isCustomer) {
      setActiveRequestCount(0);
      return undefined;
    }
    let stop = false;
    const load = () => api.get("/problems/mine")
      .then((response) => {
        if (stop) return;
        const active = (response.data || []).filter(
          (problem) => !["completed", "cancelled"].includes(problem.status),
        );
        setActiveRequestCount(active.length);
      })
      .catch(() => {});
    load();
    const timer = setInterval(load, 12000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [isCustomer, user?.id]);

  useEffect(() => {
    if (!user?.id || isAdmin) return undefined;
    const loadUnread = () => api.get("/messages/unread-count").then((r) => setUnreadMessages(r.data?.count || 0)).catch(() => {});
    loadUnread();
    const timer = setInterval(loadUnread, 12000);
    window.addEventListener("messages:read", loadUnread);
    return () => {
      clearInterval(timer);
      window.removeEventListener("messages:read", loadUnread);
    };
  }, [user?.id, isAdmin]);

  // Outside-click for the avatar dropdown.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  // Keep overlay state in sync with navigation, auth changes and the desktop breakpoint.
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.search, loc.hash, user?.id, setOpen]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const onChange = () => { if (desktop.matches) setOpen(false); };
    onChange();
    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, [setOpen]);
  useEffect(() => {
    if (!open) return;
    const toggle = toggleRef.current;
    const header = headerRef.current;
    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    mobileMenuRef.current?.querySelector("a, button")?.focus();
    const focusable = () => [...headerRef.current.querySelectorAll('[data-testid="brand-link"], [data-testid="mobile-header-chat-link"], [data-testid="nav-menu-toggle"], #mobile-navigation a, #mobile-navigation button')];
    const onKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const onFocus = event => {
      if (!headerRef.current?.contains(event.target)) toggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocus);
      if (window.matchMedia("(min-width: 1024px)").matches) header?.querySelector('[data-testid="brand-link"]')?.focus();
      else toggle?.focus();
    };
  }, [open, setOpen]);

  const currentTab = new URLSearchParams(loc.search).get("tab") ||
    (loc.pathname.startsWith("/expert") ? "overview" : null);

  const doLogout = async () => {
    setMenuOpen(false);
    setOpen(false);
    await logout();
    nav("/");
  };

  const handleMainNavClick = (item, placement) => {
    setOpen(false);
    if (item.primary) {
      trackEvent("header_create_request_clicked", {
        metadata: { route: loc.pathname, placement },
      });
    }
  };

  if (!ready) return <HeaderSkeleton/>;

  return (
    <header ref={headerRef} className={`site-header sticky top-0 bg-white/95 px-2 pt-2 backdrop-blur-md ${open ? "site-header-open" : "z-40"}`} data-testid="nav-header">
      {open && <div className="site-nav-backdrop" aria-hidden="true" onClick={() => setOpen(false)}/> }
      <div className="site-header-bar mx-auto flex h-16 w-full min-w-0 max-w-[1400px] items-center justify-between gap-2 rounded-xl border border-brand-line bg-white px-3 shadow-sm sm:gap-3 sm:px-6">
        <Link to="/" onClick={() => setOpen(false)} className="flex min-w-0 max-w-[calc(100%_-_88px)] flex-shrink items-center sm:max-w-none sm:flex-shrink-0" data-testid="brand-link" aria-label="SpørgMig forside">
          <BrandLogo/>
        </Link>

        {/* Desktop primary nav */}
        {isExpert ? (
          <nav className="hidden lg:flex items-center gap-1 text-sm flex-1 justify-center" data-testid="expert-primary-nav">
            {EXPERT_PRIMARY.map((n) => {
              const active = currentTab === n.tab;
              return (
                <Link
                  key={n.tab}
                  to={`/expert?tab=${n.tab}`}
                  data-testid={n.testid}
                  data-active={active ? "true" : undefined}
                  aria-current={active ? "page" : undefined}
                  className={`relative inline-flex min-h-16 items-center gap-1.5 px-3 transition-colors ${active ? "text-brand-green after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-brand-green" : "text-brand-body hover:text-brand-ink"}`}
                >
                  {n.label}
                  {n.badge && reqCount > 0 && (
                    <span
                      className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-fresh px-1 text-[10px] font-semibold text-white"
                      data-testid="requests-badge"
                    >{reqCount}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        ) : (
          <nav className="hidden lg:flex items-center gap-4 text-sm">
            {(isCustomer ? CUSTOMER_NAV : PUBLIC_NAV).map((n) => (
              <NavLink
                key={n.to}
                to={n.to === "/beskriv-problem" ? requestUrl : n.to}
                onClick={() => handleMainNavClick(n, "desktop")}
                data-testid={n.testid}
                className={({ isActive }) => n.primary
                  ? "btn-primary header-primary inline-flex items-center justify-center"
                  : `relative inline-flex min-h-16 items-center whitespace-nowrap transition-colors ${isActive ? "font-medium text-brand-green after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-brand-green" : n.quiet ? "text-brand-body hover:text-brand-green" : "text-brand-body hover:text-brand-green"}`}
              >
                {n.label}
                {n.requests && activeRequestCount > 0 && (
                  <span className="ml-1.5 inline-flex h-[19px] min-w-[19px] items-center justify-center rounded-full bg-brand-green px-1 text-[10px] font-semibold text-white">
                    {activeRequestCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Right-side controls */}
        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          {user ? (
            <>
              {!isAdmin && <Link to="/beskeder" className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-brand-green hover:bg-brand-soft" aria-label="Beskeder" data-testid="header-chat-link"><MessageCircle size={20}/>{unreadMessages > 0 && <span className="absolute right-0 top-0 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-fresh px-1 text-[9px] font-bold text-white">{unreadMessages}</span>}</Link>}
              {isExpert || isUnapprovedExpert ? (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-brand-soft transition-colors"
                    data-testid="expert-user-menu-toggle"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                  >
                    <ProfileAvatar photoUrl={visibleExpertProfile?.photo_url} name={user.name} size="header"/>
                    <span className="text-sm text-brand-ink max-w-[8rem] truncate">{user.name?.split(" ")[0]}</span>
                    <ChevronDown size={14} className={`text-brand-muted transition-transform ${menuOpen ? "rotate-180" : ""}`}/>
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-brand-line rounded-xl shadow-lg py-1 z-50" role="menu" data-testid="expert-user-menu">
                      <MenuLink to={expertProfilePath} onClick={() => setMenuOpen(false)} icon={<UserCircle2 size={14}/>} testid="menu-profile">Min profil</MenuLink>
                      {isPendingExpert && <MenuLink to="/expert/afventer-godkendelse" onClick={() => setMenuOpen(false)} icon={<MessageCircle size={14}/>} testid="menu-profile-status">Profilstatus</MenuLink>}
                      <MenuLink to={expertSettingsPath} onClick={() => setMenuOpen(false)} icon={<Settings size={14}/>} testid="menu-settings">Indstillinger</MenuLink>
                      {isExpert && visibleExpertProfile?.id && (
                        <MenuLink to={`/eksperter/${visibleExpertProfile.id}`} target="_blank" onClick={() => setMenuOpen(false)} icon={<ExternalLink size={14}/>} testid="menu-public-profile">Se offentlig profil</MenuLink>
                      )}
                      <div className="my-1 border-t border-brand-soft"/>
                      <button
                        onClick={doLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#991B1B] hover:bg-[#FBE9E9]"
                        role="menuitem"
                        data-testid="menu-logout"
                      >
                        <LogOut size={14}/> Log ud
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {isCustomer && <ProfileMenu name={user.name} photoUrl={user.photo_url} profileTo="/profil" settingsTo="/indstillinger" onLogout={doLogout} testId="customer-profile-menu"/>}
                  {isAdmin && <><Link to="/admin" className="text-sm text-brand-ink hover:underline flex items-center gap-1.5" data-testid="nav-dashboard"><UserIcon size={16}/> Admin</Link><button onClick={doLogout} className="text-sm text-[#991B1B]">Log ud</button></>}
                </>
              )}
            </>
          ) : (
            <>
              <Link to="/log-ind" className="text-sm text-brand-ink hover:underline" data-testid="nav-login">Log ind</Link>
              <Link to="/opret-bruger" className="inline-flex min-h-10 items-center rounded-lg border border-brand-line px-4 text-sm font-medium text-brand-ink hover:border-brand-fresh hover:text-brand-fresh" data-testid="nav-register">Kom i gang</Link>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center lg:hidden">
          {user && !isAdmin && (
            <Link
              to="/beskeder"
              className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand-green hover:bg-brand-soft"
              aria-label={unreadMessages > 0 ? `Beskeder, ${unreadMessages} ulæste` : "Beskeder"}
              data-testid="mobile-header-chat-link"
              onClick={() => setOpen(false)}
            >
              <MessageCircle size={21} />
              {unreadMessages > 0 && (
                <span className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-fresh px-1 text-[9px] font-bold text-white">
                  {unreadMessages}
                </span>
              )}
            </Link>
          )}
          <button ref={toggleRef} type="button" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full hover:bg-brand-soft" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label={open ? "Luk menu" : "Åbn menu"} aria-expanded={open} aria-controls="mobile-navigation">
            {open ? <X size={24} aria-hidden="true"/> : <Menu size={24} aria-hidden="true"/>}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-navigation" ref={mobileMenuRef} className="site-mobile-menu lg:hidden" aria-label="Mobilnavigation" data-testid="mobile-menu">
          {isExpert ? (
            <>
              {EXPERT_PRIMARY.map((n) => (
                <Link key={n.tab} to={`/expert?tab=${n.tab}`} onClick={() => setOpen(false)} aria-current={currentTab === n.tab ? "page" : undefined} className="site-mobile-link" data-testid={`mobile-${n.testid}`}>
                  <span>{n.label}</span>
                  {n.badge && reqCount > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-brand-fresh text-white">{reqCount}</span>}
                </Link>
              ))}
              <div className="site-mobile-account">
                <Link to="/expert?tab=profile" onClick={() => setOpen(false)} data-testid="mobile-menu-profile">Min profil</Link>
                <Link to="/expert?tab=settings" onClick={() => setOpen(false)} data-testid="mobile-menu-settings">Indstillinger</Link>
                {visibleExpertProfile?.id && <Link to={`/eksperter/${visibleExpertProfile.id}`} target="_blank" onClick={() => setOpen(false)} data-testid="mobile-menu-public-profile">Se offentlig profil</Link>}
                <button onClick={doLogout} className="text-left flex items-center gap-1.5 text-[#991B1B]" data-testid="mobile-menu-logout">
                  <LogOut size={14}/> Log ud
                </button>
              </div>
            </>
          ) : (
            <>
              {(isCustomer ? MOBILE_CUSTOMER_NAV : MOBILE_PUBLIC_NAV).map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to === "/beskriv-problem" ? requestUrl : n.to}
                  onClick={() => handleMainNavClick(n, "mobile")}
                  aria-current={n.auxiliary ? (loc.pathname === "/" && loc.hash === "#saadan-virker-det" ? "location" : "false") : undefined}
                  className={() => n.primary ? "btn-primary site-mobile-primary" : "site-mobile-link"}
                  data-testid={`mobile-${n.testid}`}
                >
                  <span>{n.label}</span>
                  {n.requests && activeRequestCount > 0 && (
                    <span className="inline-flex h-[19px] min-w-[19px] items-center justify-center rounded-full bg-brand-green px-1 text-[10px] font-semibold text-white">
                      {activeRequestCount}
                    </span>
                  )}
                </NavLink>
              ))}
              <div className="site-mobile-account">
              {user ? (
                <>
                  {isCustomer && <><Link to="/profil" onClick={() => setOpen(false)}>Min profil</Link><Link to="/indstillinger" onClick={() => setOpen(false)}>Indstillinger</Link></>}
                  {isUnapprovedExpert && (
                    <>
                      <Link to={expertProfilePath} onClick={() => setOpen(false)} data-testid="mobile-menu-profile">
                        {isPendingExpert ? "Min profil" : "Opret ekspertprofil"}
                      </Link>
                      {isPendingExpert && <Link to="/expert/afventer-godkendelse" onClick={() => setOpen(false)} data-testid="mobile-menu-profile-status">Profilstatus</Link>}
                      <Link to="/expert/indstillinger" onClick={() => setOpen(false)} data-testid="mobile-menu-settings">Indstillinger</Link>
                    </>
                  )}
                  {isAdmin && <Link to="/admin" onClick={() => setOpen(false)}>Admin</Link>}
                  <button onClick={doLogout} className="text-left flex items-center gap-1.5" data-testid="mobile-nav-logout">
                    <LogOut size={14}/> Log ud
                  </button>
                </>
              ) : (
                <>
                  <Link to="/log-ind" onClick={() => setOpen(false)}>Log ind</Link>
                  <Link to="/opret-bruger" onClick={() => setOpen(false)}>Kom i gang</Link>
                </>
              )}
              </div>
            </>
          )}
        </nav>
      )}
    </header>
  );
}

function MenuLink({ to, target, onClick, icon, children, testid }) {
  return (
    <Link
      to={to}
      target={target}
      rel={target === "_blank" ? "noreferrer" : undefined}
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 text-sm text-brand-ink hover:bg-brand-soft"
      role="menuitem"
      data-testid={testid}
    >
      {icon}{children}
    </Link>
  );
}
